import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { AlertTriangle, TrendingUp, TrendingDown, RefreshCw, X } from 'lucide-react';

/**
 * SlippageModal - Shows when price moves beyond threshold during transaction
 */
const SlippageModal = ({ isOpen, onClose, onConfirm, slippageData, formatPrice }) => {
  // Return null if no data
  if (!slippageData) {
    return null;
  }
  
  const slippagePercent = slippageData.slippagePercent;
  const oldPrice = slippageData.oldPrice;
  const newPrice = slippageData.newPrice;
  const direction = slippageData.direction;
  const isUp = direction === 'up';

  // Styling based on direction
  const trendColor = isUp ? 'text-green-400' : 'text-red-400';
  const bgColor = isUp ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/50';
  const gradientClass = isUp 
    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500' 
    : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="slippage-modal-backdrop"
        >
          <motion.div
            className="bg-[#0a0a0a] border-2 border-yellow-500 rounded-2xl p-6 max-w-sm w-full"
            initial={{ scale: 0.8, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 20 }}
            data-testid="slippage-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
                <h3 className="font-anton text-xl text-yellow-400">PRICE CHANGED!</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="slippage-modal-close"
              >
                <X className="w-5 h-5 text-white/50" />
              </button>
            </div>

            {/* Warning Message */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
              <p className="text-white/80 text-sm">
                The $SLAP price moved{' '}
                <span className={`font-bold ${trendColor}`}>
                  {slippagePercent}%
                </span>{' '}
                since you clicked play.
              </p>
              <p className="text-white/60 text-xs mt-2">
                This exceeds the 5% slippage protection threshold.
              </p>
            </div>

            {/* Price Comparison */}
            <div className="space-y-3 mb-6">
              {/* Old Price */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <span className="text-white/60 text-sm">Your Price:</span>
                <span className="font-mono text-white/80 line-through">
                  {formatPrice ? formatPrice(oldPrice) : oldPrice}
                </span>
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                {isUp ? (
                  <TrendingUp className="w-6 h-6 text-green-400" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-400" />
                )}
              </div>

              {/* New Price */}
              <div className={`flex items-center justify-between p-3 rounded-lg border-2 ${bgColor}`}>
                <span className={`text-sm font-medium ${trendColor}`}>
                  New Price:
                </span>
                <span className={`font-mono font-bold ${trendColor}`}>
                  {formatPrice ? formatPrice(newPrice) : newPrice}
                </span>
              </div>
            </div>

            {/* Impact Explanation */}
            <div className="text-center text-xs text-white/50 mb-4">
              {isUp ? (
                <p>Price went UP - you&apos;ll pay <span className="text-red-400">fewer tokens</span> for the same USD value</p>
              ) : (
                <p>Price went DOWN - you&apos;ll pay <span className="text-red-400">more tokens</span> for the same USD value</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                data-testid="slippage-cancel-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={onConfirm}
                className={`flex-1 font-anton ${gradientClass} text-white`}
                data-testid="slippage-confirm-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                CONFIRM NEW PRICE
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SlippageModal;
